import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { z } from "zod";
import { TaskNavigationSectionSchema } from "@nexus/contracts";
import { useAuth } from "@/features/auth/hooks/useAuth";
import { preflightReportQueryOptions } from "@/lib/queries";
import { PreFlightReportPage } from "@/features/preflight/pages/PreFlightReportPage";
import { useTaskSectionJump } from "@/features/task-progress/hooks/useTaskSectionJump";

/**
 * Deep-linkable Report view (KTD7). The loader warms the detail cache; a foreign/missing
 * id surfaces as a query error and the page renders the not-found treatment (403 and 404
 * are intentionally indistinguishable — tenant isolation is enforced server-side).
 */
export const Route = createFileRoute(
  "/_authenticated/pre-flight/reports/$reportId",
)({
  validateSearch: z.object({
    section: TaskNavigationSectionSchema.optional().catch(undefined),
    export: z.literal("pdf").optional().catch(undefined),
  }),
  loader: ({ context, params }) => {
    const tenantId = useAuth.getState().activeTenantId;
    // ensureQueryData ignores `enabled: false`, so guard here to avoid a
    // spurious request (and a __none__-keyed cache entry) before auth resolves.
    if (!tenantId) return null;
    return context.queryClient
      .ensureQueryData(preflightReportQueryOptions(tenantId, params.reportId))
      .catch(() => null);
  },
  component: PreFlightReportRoute,
});

function PreFlightReportRoute() {
  const { reportId } = Route.useParams();
  const navigate = useNavigate();
  useTaskSectionJump();
  return (
    <div className="mx-auto w-full max-w-5xl p-6">
      <PreFlightReportPage
        reportId={reportId}
        onBack={() => void navigate({ to: "/pre-flight" })}
      />
    </div>
  );
}
